import { Label } from '@connected/form-fields/components/label';
import { Button } from '@connected/ui/components/button';
import { Input } from '@connected/ui/components/input';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@connected/ui/components/popover';
import { ScrollArea } from '@connected/ui/components/scroll-area';
import { cn, hexToRgba } from '@connected/ui/lib/utils';
import { PopoverClose } from '@radix-ui/react-popover';
import { ArrowDown, ArrowUp, CheckIcon, ChevronDownIcon, X } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import type { HandleChange, Option, OptionValue } from './types';

import { CheckboxField } from './checkbox';

export type OptionItemProps<TOptionValue extends OptionValue> = {
    name?: string;
    option: Option<TOptionValue>;
    selected: boolean;
    onSelect: () => void;
    color?: string;
    disabled?: boolean;
};

export type OptionFieldProps<TOptionValue extends OptionValue> = {
    name?: string;
    dropdown?: boolean;
    className?: string;
    maxItemsBeforeScroll?: number;
    itemHeight?: number;
    options: Array<Option<TOptionValue>>;
    closeOnClick?: boolean;
    render: React.FC<OptionItemProps<TOptionValue>>;
    placeholder?: string;
    emptyOptionsMessage?: string;
    search?: boolean;
    disabled?: boolean;
    hideSelected?: boolean;
};

export type SingleOptionFieldProps<TOptionValue extends OptionValue> = {
    state: { value: TOptionValue };
    handleChange: HandleChange<TOptionValue>;
    placeholder?: string;
    inputClassname?: string;
} & OptionFieldProps<TOptionValue>;

export type MultipleOptionFieldProps<TOptionValue extends OptionValue> = {
    state: { value: TOptionValue[] };
    handleChange: HandleChange<Array<TOptionValue>>;
} & OptionFieldProps<TOptionValue>;

export function SingleOptionItem<TOptionValue extends OptionValue>({
    name,
    option,
    selected,
    onSelect,
    color,
    disabled = false,
}: OptionItemProps<TOptionValue>) {
    return (
        <Label
            className={cn(
                'group grid min-h-10 cursor-pointer grid-flow-col items-center justify-start gap-2 pl-2',
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            <input
                type="radio"
                checked={selected}
                onChange={onSelect}
                name={name}
                className="peer min-h-4 min-w-4 max-h-4 max-w-4 appearance-none rounded-full border border-input-border ring-offset-background transition-all checked:border-[4px] checked:border-brand-primary hover:border-input-border-hover checked:hover:border-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                    borderColor: selected && color ? color : undefined,
                }}
                disabled={disabled}
            />
            <span>{option.label}</span>
        </Label>
    );
}

export function SingleCheckOptionItem<TOptionValue extends OptionValue>({
    name,
    option,
    selected,
    onSelect,
    color,
    disabled = false,
}: OptionItemProps<TOptionValue>) {
    return (
        <Label
            className={cn(
                'group grid min-h-10 h-fit cursor-pointer grid-flow-col items-center justify-between gap-2  hover:bg-background-tertiary p-2 rounded-md w-full',
                disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
                selected && 'bg-background-tertiary',
            )}
        >
            {/* Added hidden class to make option still selectable, but use checkmark instead */}
            <input
                type="radio"
                checked={selected}
                onChange={onSelect}
                name={name}
                className="hidden peer min-h-4 min-w-4 max-h-4 max-w-4 appearance-none rounded-full border border-input-border ring-offset-background transition-all checked:border-[4px] checked:border-brand-primary hover:border-input-border-hover checked:hover:border-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                    borderColor: selected && color ? color : undefined,
                }}
                disabled={disabled}
            />
            <span className="text-text-primary text-start text-sm font-normal">{option.label}</span>
            <div className="flex h-full w-5">
                {selected && (
                    <CheckIcon className="size-5 text-primary" />
                )}
            </div>
        </Label>
    );
}

export function SingleOptionSumUpItem<TOptionValue extends OptionValue>({
    name,
    option,
    selected,
    onSelect,
    color,
    disabled = false,
}: OptionItemProps<TOptionValue>) {
    return (
        <Label
            className={cn(
                'group flex flex-row max-h-fit cursor-pointer items-center gap-2 py-2 pl-2',
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            <input
                type="radio"
                checked={selected}
                onChange={onSelect}
                name={name}
                className="peer min-h-4 min-w-4 max-h-4 max-w-4 appearance-none rounded-full border border-input-border ring-offset-background transition-all checked:border-[4px] checked:border-brand-primary hover:border-input-border-hover checked:hover:border-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                    borderColor: selected && color ? color : undefined,
                }}
                disabled={disabled}
            />
            <div className="flex flex-col gap-1">
                {typeof option.label === 'string'
                    ? (
                            <span>{option.label}</span>
                        )
                    : (
                            option.label.map((label, index) => (
                                <span key={index}>{label}</span>
                            ))
                        )}
            </div>
        </Label>
    );
}

export function SingleHorizontalOptionItem<TOptionValue extends OptionValue>({
    name,
    option,
    selected,
    onSelect,
    color,
    disabled = false,
}: OptionItemProps<TOptionValue>) {
    return (
        <Label
            className={cn(
                'group flex flex-row h-10 w-full cursor-pointer items-center justify-between p-4 gap-2 border first:rounded-l-md last:rounded-r-md transition-all duration-100 ',
                disabled && 'cursor-not-allowed opacity-50',
                selected ? 'bg-background-secondary' : 'border-input-border hover:border-input-border-hover ',
            )}
        >
            <span className="whitespace-nowrap">{option.label}</span>
            <input
                type="radio"
                checked={selected}
                onChange={onSelect}
                name={name}
                className="peer min-h-4 min-w-4 appearance-none rounded-full border border-input-border ring-offset-background transition-all checked:border-[4px] checked:border-brand-primary hover:border-input-border-hover checked:hover:border-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                    borderColor: selected && color ? color : undefined,
                }}
                disabled={disabled}
            />
        </Label>
    );
}

export function SingleHorizontalOptionItemWithoutDot<
    TOptionValue extends OptionValue,
>({ name, option, selected, onSelect, disabled = false }: OptionItemProps<TOptionValue>) {
    return (
        <Label
            className={cn(
                'group flex h-10 w-full cursor-pointer items-center justify-center gap-2 border border-input-border first:rounded-l-md last:rounded-r-md',
                selected ? 'bg-background-secondary' : null,
                disabled && 'cursor-not-allowed opacity-50',
            )}
        >
            <input
                hidden
                type="radio"
                className="peer h-4 w-4 appearance-none rounded-full border border-input-border ring-offset-background transition-all checked:border-[4px] checked:border-brand-primary hover:border-input-border-hover checked:hover:border-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                name={name}
                checked={selected}
                onChange={onSelect}
                disabled={disabled}
            />
            <span className="text-wrap">{option.label}</span>
        </Label>
    );
}

export function MultipleOptionItem<TOptionValue extends OptionValue>({
    name,
    option,
    selected,
    onSelect,
    color,
    disabled = false,
}: OptionItemProps<TOptionValue>) {
    return (
        <CheckboxField
            value={selected}
            label={option.label}
            onChange={onSelect}
            name={name}
            style={{
                color: color || undefined,
            }}
            className="h-fit min-h-10"
            disabled={disabled}
        />
    );
}

const inputClasses = cn(
    // Layout
    'flex h-10 w-full items-center justify-between gap-2 rounded-md',
    // Background & Border
    'border border-input-border bg-transparent border-box',
    // Text
    'px-3 py-2 text-sm',
    // Transition
    'transition duration-100',
    // Placeholder & Hover
    'placeholder:text-muted-foreground placeholder:font-normal hover:border-input-border-hover hover:shadow-input-hover hover:bg-inherit',
    // Focus
    'focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    // Disabled
    'disabled:cursor-not-allowed disabled:opacity-50',
);

export function SingleOptionField<TOptionValue extends OptionValue>({
    name,
    state: { value },
    handleChange,
    options,
    render: Render,
    dropdown = false,
    className,
    maxItemsBeforeScroll = 10,
    itemHeight = 40,
    placeholder,
    closeOnClick = false,
    disabled = false,
    emptyOptionsMessage = '',
    search = false,
    inputClassname = '',
    hideSelected = false,
}: SingleOptionFieldProps<TOptionValue>) {
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const Wrapper
        = options.length > maxItemsBeforeScroll ? ScrollArea : Fragment;
    const rows = useMemo(() => {
        const Trigger = dropdown && closeOnClick ? PopoverClose : Fragment;

        const SupWrapper = dropdown ? 'div' : Fragment;

        const filteredOptions = options.filter(
            option => option.label?.toString()?.toLowerCase()?.includes(searchValue.toLowerCase()),
        ).filter(option => !hideSelected || option.value !== value);

        return (
            <Wrapper
                {...(Wrapper === ScrollArea
                    ? {
                            style: {
                                height: `${maxItemsBeforeScroll * itemHeight}px`,
                            },
                            className: 'scroll-area-nested',
                        }
                    : {})}
            >
                <SupWrapper {...(dropdown && { className: 'flex flex-col gap-1' })}>
                    {(search && dropdown) && (
                        <Input
                            className="mb-2"
                            placeholder="Zoeken..."
                            onChange={e => setSearchValue(e.target.value)}
                            disabled={disabled}
                            value={searchValue}
                        />
                    )}
                    {filteredOptions.map(option => (
                        <Trigger key={`${String(option.value)}-${option.label}`}>
                            <Render
                                option={option}
                                selected={option.value === value}
                                onSelect={() => {
                                    handleChange(option.value);
                                }}
                                name={name}
                                color={option.color}
                                disabled={disabled}
                            />
                        </Trigger>
                    ))}

                    {(filteredOptions.length === 0 && emptyOptionsMessage) && (
                        <span className={cn('text-text-quaternary ', dropdown && 'text-center')}>{emptyOptionsMessage}</span>
                    )}
                </SupWrapper>
            </Wrapper>
        );
    }, [Render, Wrapper, closeOnClick, disabled, dropdown, emptyOptionsMessage, handleChange, hideSelected, itemHeight, maxItemsBeforeScroll, name, options, search, searchValue, value]);

    if (dropdown) {
        return (
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        // className={cn('flex items-center justify-between gap-2', className)}
                        className={cn(inputClasses, inputClassname)}
                        disabled={disabled}
                    >

                        <span className={cn('truncate', {
                            'text-text-placeholder ': value === undefined || value === null || value === '',
                        })}
                        >
                            {options.find(option => option.value === value)
                                ?.label || placeholder}
                        </span>
                        <ChevronDownIcon
                            className={cn('transition-transform', {
                                'rotate-180': open,
                            })}
                            size={24}
                        />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '200px' }}
                    className="flex flex-col"
                    onOpenAutoFocus={e => e.preventDefault()}
                    onWheel={e => e.stopPropagation()}
                    onTouchMove={e => e.stopPropagation()}
                >
                    {
                        Wrapper === ScrollArea && (
                            <ArrowUp size={14} className="mx-auto mb-1 text-text-quaternary" />
                        )
                    }
                    <div className="flex flex-col gap-1">
                        {rows}
                    </div>
                    {
                        Wrapper === ScrollArea && (
                            <ArrowDown size={14} className="mx-auto mt-1 text-text-quaternary" />
                        )
                    }
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <div className={cn(['flex flex-col overflow-x-auto relative', className])}>
            {rows}
        </div>
    );
}

export function MultipleOptionField<TOptionValue extends OptionValue>({
    name,
    state: { value },
    handleChange,
    options,
    render: Render,
    dropdown = false,
    disabled = false,
    className,
    maxItemsBeforeScroll = 5,
    itemHeight = 40,
    closeOnClick,
    placeholder = '',
    emptyOptionsMessage = '',
    search = false,
    hideSelected = false,
}: MultipleOptionFieldProps<TOptionValue>) {
    const Trigger = closeOnClick ? PopoverClose : Fragment;
    const [searchValue, setSearchValue] = useState('');
    const [open, setOpen] = useState(false);

    const selectedOptions = useMemo(
        () => options.filter(option => value.includes(option.value)),
        [options, value],
    );

    const filteredOptionsWithSelected = useMemo(() => {
        return options.filter(option => option.label?.toString()?.toLowerCase()?.includes(searchValue.toLowerCase()))
            .filter(option => !hideSelected || !value.includes(option.value));
    }, [options, searchValue, hideSelected, value]);

    const rows = useMemo(() => {
        const Wrapper = options?.length > maxItemsBeforeScroll ? 'div' : Fragment;
        return (
            <Wrapper>
                {(search && dropdown) && (
                    <Input
                        className="flex m-2"
                        placeholder="Zoeken..."
                        onChange={e => setSearchValue(e.target.value)}
                        disabled={disabled}
                        value={searchValue}
                    />
                )}
                <div
                    className="overflow-y-auto"
                    style={dropdown
                        ? {
                                maxHeight: `${maxItemsBeforeScroll * itemHeight}px`,
                            }
                        : {}}
                >
                    <div className="flex flex-col">
                        {filteredOptionsWithSelected.map(option => (
                            <Trigger key={`${String(option.value)}-${option.label}`}>
                                <Render
                                    option={option}
                                    selected={value.includes(option.value)}
                                    onSelect={() => {
                                        if (!value.includes(option.value)) {
                                            return handleChange([
                                                ...value,
                                                option.value,
                                            ]);
                                        }
                                        handleChange(
                                            value.filter(
                                                item => item !== option.value,
                                            ),
                                        );
                                    }}
                                    name={name}
                                    color={option.color}
                                    disabled={disabled}
                                />
                            </Trigger>
                        ))}
                    </div>
                </div>

                {(filteredOptionsWithSelected.length === 0 && emptyOptionsMessage) && (
                    <span className={cn('text-text-quaternary ', dropdown && 'text-center')}>{emptyOptionsMessage}</span>
                )}
            </Wrapper>
        );
    }, [Render, Trigger, disabled, dropdown, emptyOptionsMessage, filteredOptionsWithSelected, handleChange, itemHeight, maxItemsBeforeScroll, name, search, searchValue, value]);

    if (dropdown) {
        return (
            <Popover onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        className={cn(inputClasses, 'h-fit', open && 'border-input-border-hover shadow-input-hover bg-inherit')}
                        disabled={disabled}
                    >
                        <div className={cn('flex item-center gap-2 flex-1 max-w-full')}>
                            <div className="flex flex-wrap gap-2 flex-1 max-w-full text-wrap text-start">
                                {selectedOptions.map(option => (
                                    <span
                                        key={String(option.value)}
                                        className="flex items-center gap-2 bg-background-secondary rounded-md px-3 py-1 border border-1 border-border text-text-secondary text-sm font-normal"
                                    >
                                        {option.label}
                                        <div>
                                            <Button
                                                asChild
                                                variant="outline"
                                                className={cn('p-0 h-4 w-4 border-0 text-text-quaternary transition duration-100 ')}
                                                onClick={(e) => {
                                                    e.preventDefault(); // prevent click event for toggling popover
                                                    handleChange(
                                                        value.filter(
                                                            item => item !== option.value,
                                                        ),
                                                    );
                                                }}
                                            >
                                                <X className="size-4" />
                                            </Button>
                                        </div>
                                    </span>
                                ))}
                                {selectedOptions.length === 0 && (
                                    <span className="flex items-center text-text-placeholder">{placeholder}</span>
                                )}
                            </div>
                            <div className="flex items-center">
                                <ChevronDownIcon
                                    className={cn('transition-transform', {
                                        'rotate-180': open,
                                    })}
                                    size={24}
                                />
                            </div>
                        </div>
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    side="bottom"
                    avoidCollisions={true}
                    style={{ width: 'var(--radix-popover-trigger-width)', minWidth: '200px' }}
                    className="flex flex-col"
                    onOpenAutoFocus={e => e.preventDefault()}
                >
                    {rows}
                </PopoverContent>
            </Popover>
        );
    }

    return <div className={cn(['flex flex-col', className])}>{rows}</div>;
}

export function IB22SingleOptionItem<TOptionValue extends OptionValue>({
    name,
    option,
    selected,
    onSelect,
    color,
    disabled = false,
}: OptionItemProps<TOptionValue>) {
    return (
        <Label
            className={cn(
                'group flex flex-row w-full cursor-pointer items-center justify-between px-4 py-2 gap-2 border border-border first:rounded-l-md last:rounded-r-md transition-all duration-100',
                disabled && 'cursor-not-allowed opacity-50',
                selected ? 'bg-background-secondary text-sm font-medium' : 'border-input-border hover:border-input-border-hover',
            )}
            style={{
                backgroundColor: selected && color ? hexToRgba(color, 0.15)! : undefined,
            }}

        >
            <span className={cn('text-text-secondary', selected ? 'text-sm font-medium' : ' font-normal text-sm')}>
                {option.label}
            </span>
            <input
                type="radio"
                checked={selected}
                onChange={onSelect}
                name={name}
                className="peer min-h-4 min-w-4 appearance-none rounded-full border-2 border-input-border ring-offset-background transition-all checked:border-[4px] checked:border-brand-primary hover:border-input-border-hover checked:hover:border-brand-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                    borderColor: color,

                }}
                disabled={disabled}
            />
        </Label>
    );
}

export function ClearFields({
    onClear,
    className,
}:
{
    onClear: () => void;
    className?: string;
}) {
    return (
        <button
            type="button"
            className={cn('absolute top-0 right-0 text-text-primary bg-background-quaternary hover:text-text-secondary p-0.5 rounded-sm', className)}
            onClick={(e) => {
                e.stopPropagation();
                onClear();
            }}
        >
            <X size={15} />
        </button>
    );
}
